// src/pages/Payment/PendingPage.jsx
// ⏳ Página de pagamento pendente (PIX/Boleto)
// Criado em: 16/03/2026
// Versão: 1.0

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Clock, 
  Copy, 
  Check, 
  Smartphone,
  FileText,
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  CreditCard,
  Download,
  HelpCircle
} from 'lucide-react';

import { usePayment } from '../../contexts/PaymentContext/PaymentContext';
import { formatBRL } from '../../utils/priceFormatter';

/**
 * Página para pagamentos pendentes (PIX e Boleto)
 * Exibe QR Code PIX ou linha digitável do boleto
 */
export default function PaymentPendingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const payment = usePayment();
  
  // Estados
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutos em segundos
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);

  // Parâmetros
  const method = searchParams.get('method') || 'pix';
  const amount = searchParams.get('amount') || '197.00';
  const planId = searchParams.get('plan') || 'professional';

  // Mock data (será substituído pela API real)
  const mockPixCode = '00020126360014BR.GOV.BCB.PIX0114+55119999999995204000053039865406123.45ipad.imoveispro.com.br62140510ImoveisPro6304';
  const mockBoletoCode = '34191.79001 01043.510047 91020.150008 7 84570026000';
  const mockBoletoUrl = '#';

  // ============================================
  // ⏱️ TIMER DE EXPIRAÇÃO
  // ============================================
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // ============================================
  // 🔄 VERIFICAR STATUS DO PAGAMENTO
  // ============================================
  useEffect(() => {
    const checkStatus = async () => {
      setCheckingStatus(true);
      try {
        // Simular verificação a cada 5 segundos
        const status = await payment.checkPaymentStatus('mock_payment_id');
        
        if (status.status === 'approved') {
          setPaymentConfirmed(true);
          // Redirecionar para sucesso após 2 segundos
          setTimeout(() => {
            navigate('/payment/success');
          }, 2000);
        }
      } catch (error) {
        console.error('Erro ao verificar status:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    const interval = setInterval(checkStatus, 5000);
    
    return () => clearInterval(interval);
  }, [payment, navigate]);

  // ============================================
  // 🎯 AÇÕES
  // ============================================

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadBoleto = () => {
    window.open(mockBoletoUrl, '_blank');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ============================================
  // 🎨 RENDERIZAÇÃO POR MÉTODO
  // ============================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-center relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full" />
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full" />
          
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 3, ease: 'linear' }}
            className="w-16 h-16 mx-auto mb-3"
          >
            {method === 'pix' ? (
              <Smartphone className="w-16 h-16 text-white" />
            ) : (
              <FileText className="w-16 h-16 text-white" />
            )}
          </motion.div>
          
          <h1 className="text-2xl font-bold text-white mb-1">
            {method === 'pix' ? 'Pagamento PIX' : 'Boleto Bancário'}
          </h1>
          <p className="text-blue-100">
            {method === 'pix' 
              ? 'Escaneie o QR Code para pagar' 
              : 'Pague o boleto em qualquer banco'}
          </p>
        </div>

        {/* Timer de expiração */}
        <div className="bg-amber-50 border-b border-amber-100 p-4">
          <div className="flex items-center justify-center gap-2 text-amber-700">
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">
              O {method === 'pix' ? 'QR Code' : 'boleto'} expira em {formatTime(timeLeft)}
            </span>
          </div>
        </div>

        {/* Conteúdo principal */}
        <div className="p-6">
          
          {/* Valor */}
          <div className="text-center mb-6">
            <p className="text-sm text-slate-500 mb-1">Valor a pagar</p>
            <p className="text-3xl font-bold text-slate-900">{formatBRL(parseFloat(amount))}</p>
            <p className="text-xs text-slate-400 mt-1">
              Plano {planId} • Assinatura mensal
            </p>
          </div>

          {/* Conteúdo específico por método */}
          <AnimatePresence mode="wait">
            {method === 'pix' ? (
              <motion.div
                key="pix"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {/* QR Code (simulado) */}
                <div className="bg-white p-4 rounded-xl border-2 border-dashed border-slate-200">
                  <div className="w-48 h-48 mx-auto bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center">
                    <span className="text-6xl">📱</span>
                  </div>
                </div>

                {/* Código PIX */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Código PIX (copia e cola)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={mockPixCode}
                      readOnly
                      className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-xs font-mono"
                    />
                    <button
                      onClick={() => handleCopy(mockPixCode)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-200 rounded-lg transition"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-500" />
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="boleto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {/* Linha digitável */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Linha digitável
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={mockBoletoCode}
                      readOnly
                      className="w-full px-4 py-3 pr-12 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono"
                    />
                    <button
                      onClick={() => handleCopy(mockBoletoCode)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-200 rounded-lg transition"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-slate-500" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Botão download */}
                <button
                  onClick={handleDownloadBoleto}
                  className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Baixar boleto em PDF
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Status da verificação */}
          <div className="mt-6 p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {checkingStatus ? (
                  <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />
                ) : (
                  <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                )}
                <span className="text-sm text-slate-600">
                  {checkingStatus 
                    ? 'Verificando pagamento...' 
                    : paymentConfirmed
                      ? 'Pagamento confirmado! Redirecionando...'
                      : 'Aguardando confirmação...'}
                </span>
              </div>
              {!checkingStatus && !paymentConfirmed && (
                <button
                  onClick={() => payment.checkPaymentStatus('mock')}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Verificar agora
                </button>
              )}
            </div>
          </div>

          {/* Instruções */}
          <div className="mt-4 p-4 bg-blue-50 rounded-xl flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-900 font-medium mb-1">
                {method === 'pix' 
                  ? 'Como pagar com PIX?' 
                  : 'Como pagar o boleto?'}
              </p>
              <p className="text-xs text-blue-700">
                {method === 'pix'
                  ? 'Abra o app do seu banco, escaneie o QR Code ou cole o código PIX.'
                  : 'Pague em qualquer banco, casa lotérica ou app. Compensação em até 3 dias úteis.'}
              </p>
            </div>
          </div>

          {/* Botão voltar */}
          <button
            onClick={() => navigate('/planos')}
            className="w-full mt-4 py-3 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Escolher outro plano
          </button>
        </div>
      </motion.div>
    </div>
  );
}